/**
 * Games data — Firestore /games collection + local cache.
 * Populated by fetchGamesByDate and get_homepage_data (server reads /games only).
 */
import { collection, getDocs, limit, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { readCache, writeCache } from '../utils/dataCache';
import { dedupeGamesForDisplay } from '../utils/gameDedupe';
import {
  filterLockableUpcomingGames,
  isDisplayLiveGame,
  isLockableUpcomingGame,
} from '../utils/gameLockability';
import { UPCOMING_TAB } from '../utils/sportBadges';
import { findLiveEventForMatchup } from '../utils/challengeWinner';

export const GAMES_CACHE_KEY = 'lockedin_games_v1';
/** Max games per date — matches server MAX_GAMES_PER_DATE safeguard. */
export const MAX_GAMES_PER_DATE = 600;
/** Upcoming feed window — matches server UPCOMING_WINDOW_DAYS (default 7). */
export const UPCOMING_WINDOW_DAYS = Math.max(
  1,
  Math.min(Number(process.env.REACT_APP_UPCOMING_WINDOW_DAYS) || 7, 60)
);

/** UTC date string YYYY-MM-DD for Firestore date queries. */
export function todayUtcDate() {
  return new Date().toISOString().slice(0, 10);
}

/** UTC date strings from today through today + (days - 1). */
export function upcomingUtcDates(days = UPCOMING_WINDOW_DAYS) {
  const count = Math.max(1, days);
  const base = new Date();
  base.setUTCHours(0, 0, 0, 0);
  const dates = [];
  for (let i = 0; i < count; i += 1) {
    const d = new Date(base);
    d.setUTCDate(base.getUTCDate() + i);
    dates.push(d.toISOString().slice(0, 10));
  }
  return dates;
}

let gamesWindowInflight = null;
let gamesWindowAllInflight = null;

async function fetchAllGamesForWindow(days = UPCOMING_WINDOW_DAYS) {
  const dates = upcomingUtcDates(days);
  const batches = await Promise.all(dates.map((dateStr) => fetchGamesByDate(dateStr)));
  return dedupeGamesForDisplay(batches.flat());
}

function mapGameDoc(doc) {
  const data = doc.data() || {};
  return {
    id: data.gameId || doc.id,
    sport: data.sport,
    league: data.league || '',
    a: data.a,
    b: data.b,
    scoreA: data.scoreA,
    scoreB: data.scoreB,
    status: data.status,
    startTime: data.startTime,
    date: data.date,
  };
}

/**
 * Read processed games from Firestore /games for a date.
 * @param {string} [dateStr] — defaults to today UTC
 */
export async function fetchGamesByDate(dateStr = todayUtcDate()) {
  const q = query(
    collection(db, 'games'),
    where('date', '==', dateStr),
    limit(MAX_GAMES_PER_DATE)
  );
  const snap = await getDocs(q);
  if (snap.size >= MAX_GAMES_PER_DATE) {
    console.warn(
      `[games] date=${dateStr} hit read limit (${MAX_GAMES_PER_DATE}); results may be truncated`
    );
  }
  return snap.docs.map(mapGameDoc);
}

/**
 * Fetch upcoming games across a UTC date window with request deduplication.
 * @param {number} [days]
 */
export async function fetchGamesForUpcomingWindow(days = UPCOMING_WINDOW_DAYS) {
  if (gamesWindowInflight) return gamesWindowInflight;

  gamesWindowInflight = (async () => {
    const games = await fetchAllGamesForWindow(days);
    return filterLockableUpcomingGames(games, { windowDays: days });
  })();

  try {
    return await gamesWindowInflight;
  } finally {
    gamesWindowInflight = null;
  }
}

/** Full /games window for cache persistence (includes live + final rows). */
export async function fetchAllGamesForCache(days = UPCOMING_WINDOW_DAYS) {
  if (gamesWindowAllInflight) return gamesWindowAllInflight;

  gamesWindowAllInflight = fetchAllGamesForWindow(days);

  try {
    return await gamesWindowAllInflight;
  } finally {
    gamesWindowAllInflight = null;
  }
}

/**
 * @returns {{ games: object[], lastUpdated: number | null }}
 */
export function loadCachedGamesSync() {
  const { data, lastUpdated } = readCache(GAMES_CACHE_KEY);
  const games = Array.isArray(data?.games) ? dedupeGamesForDisplay(data.games) : [];
  return { games, lastUpdated };
}

/** Upcoming feed only — lockable pre-game window. */
export function loadLockableUpcomingGamesSync() {
  const { games, lastUpdated } = loadCachedGamesSync();
  return {
    games: filterLockableUpcomingGames(games, { windowDays: UPCOMING_WINDOW_DAYS }),
    lastUpdated,
  };
}

let liveLookupInflight = null;

/**
 * Fresh /games read for live challenge score lookup (includes finals; not lockability-filtered).
 */
export async function fetchGamesForLiveLookup(days = UPCOMING_WINDOW_DAYS) {
  const dates = new Set(upcomingUtcDates(days));
  const yesterday = new Date();
  yesterday.setUTCDate(yesterday.getUTCDate() - 1);
  dates.add(yesterday.toISOString().slice(0, 10));

  const batches = await Promise.all([...dates].map((dateStr) => fetchGamesByDate(dateStr)));
  return dedupeGamesForDisplay(batches.flat());
}

/**
 * Resolve the latest game row for an in-progress challenge (Firestore + full cache).
 */
export async function fetchLiveGameForMatchup(matchup) {
  if (!matchup) return null;

  const { games: cached } = loadCachedGamesSync();
  const fromCache = findLiveEventForMatchup(cached, matchup);
  if (fromCache) return fromCache;

  if (liveLookupInflight) {
    const games = await liveLookupInflight;
    return findLiveEventForMatchup(games, matchup);
  }

  liveLookupInflight = fetchGamesForLiveLookup(UPCOMING_WINDOW_DAYS);
  try {
    const games = await liveLookupInflight;
    return findLiveEventForMatchup(games, matchup);
  } finally {
    liveLookupInflight = null;
  }
}

/** @internal Used when persisting games from homepage hydrate or Firestore refresh. */
export function persistGamesCache(games, lastUpdated) {
  writeCache(GAMES_CACHE_KEY, { games: dedupeGamesForDisplay(games) });
  return lastUpdated ?? Date.now();
}

/** Filter games for a sport tab (client-side) — live + lockable upcoming only. */
export function gamesForSportTab(allGames, tabLabel) {
  const games = dedupeGamesForDisplay(allGames);
  if (!Array.isArray(games)) return [];
  if (tabLabel === 'Upcoming' || tabLabel === UPCOMING_TAB) {
    return filterLockableUpcomingGames(games, { windowDays: UPCOMING_WINDOW_DAYS });
  }
  return games.filter((g) => {
    if (g.sport !== tabLabel) return false;
    return isDisplayLiveGame(g)
      || isLockableUpcomingGame(g, { windowDays: UPCOMING_WINDOW_DAYS });
  });
}
